import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { CUSTOM_FORM_LIST, LIST_VIEW_LIMIT, SUCCESS_STATUS } from "../../constants";
import { LOADING_KEYS } from "../../constants/loadingKeys";
import { useListQuery } from "../../hooks/useListQuery";
import type { ApiEnvelope } from "../../types/api";
import * as api from "./api";
import type { CreateFormPayload, CustomFormItem, CustomFormListData, FacilitiesData, UpdateFormDetailsPayload } from "./types";

export const customFormQueryKeys = {
  list: (params: { offset: number; search?: string; sortType?: string }) => [CUSTOM_FORM_LIST, params] as const,
};

function formatCustomFormItem(item: CustomFormItem): CustomFormItem {
  return {
    ...item,
    dateCreated: item.dateCreated ? format(new Date(item.dateCreated), "MMMM dd, yyyy") : "",
    dateUpdated: item.dateUpdated ? format(new Date(item.dateUpdated), "MMMM dd, yyyy") : "",
  };
}

/**
 * List/search/sort/paginate for the Custom Forms admin list screen.
 */
export function useCustomFormList(params: { offset: number; search?: string; sortType?: string }) {
  const query = useListQuery<ApiEnvelope<CustomFormListData>>({
    url: CUSTOM_FORM_LIST,
    method: "GET",
    limit: LIST_VIEW_LIMIT,
    offset: params.offset,
    search: params.search,
    sortType: params.sortType,
    loadingKey: LOADING_KEYS.GET_CUSTOM_FORM,
  });

  const customFormList: ApiEnvelope<CustomFormListData> = useMemo(() => {
    if (!query.data || query.data.status !== SUCCESS_STATUS) {
      return query.data ?? ({} as ApiEnvelope<CustomFormListData>);
    }
    const raw = query.data.data ?? {};
    return {
      ...query.data,
      data: {
        ...raw,
        forms: (raw.forms ?? []).map(formatCustomFormItem),
      },
    };
  }, [query.data]); // eslint-disable-line react-hooks/exhaustive-deps

  return { ...query, customFormList };
}

/**
 * Mutation: DELETE /forms/:id for each item (parallel, Promise.all).
 * Invalidates the list on success.
 */
export function useDeleteCustomFormsMutation() {
  const queryClient = useQueryClient();
  return useMutation<ApiEnvelope<void>[], Error, Array<{ id: number }>>({
    mutationFn: api.deleteCustomForms,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CUSTOM_FORM_LIST] });
    },
  });
}

/**
 * Mutation: POST /forms for the Create Form modal.
 *
 * Mirrors the legacy `createFormAsync` thunk's error handling: a failed
 * request resolves to the caught error object (which carries `errorMessage`)
 * instead of rejecting, since the modal never had a distinct "error" render
 * branch -- it shows the message inline via the Form Name field's
 * `customError` prop while staying on the create form, same as before.
 */
export function useCreateCustomFormMutation() {
  return useMutation<ApiEnvelope<CustomFormItem>, Error, CreateFormPayload>({
    mutationFn: async (payload) => {
      try {
        return await api.createCustomForm(payload);
      } catch (error) {
        return { ...(error as object) } as ApiEnvelope<CustomFormItem>;
      }
    },
  });
}

/**
 * Facility lookup for the Assign tab's organization picker.
 */
export function useFacilitiesByCustomerNumber(customerNumber: string | undefined) {
  return useQuery<ApiEnvelope<FacilitiesData>>({
    queryKey: ["forms", "facilities", customerNumber],
    queryFn: async () => {
      try {
        return await api.fetchFacilitiesByCustomerNumber(customerNumber as string);
      } catch (error) {
        return { ...(error as object) } as ApiEnvelope<FacilitiesData>;
      }
    },
    enabled: Boolean(customerNumber),
  });
}

/**
 * Mutation: PUT /customform/:id, used only by the Assign tab in this ticket.
 */
export function useUpdateFormDetailsMutation() {
  return useMutation<ApiEnvelope<UpdateFormDetailsPayload>, Error, UpdateFormDetailsPayload>({
    mutationFn: api.updateFormDetails,
  });
}
